// Dragging favorites and folders in the sidebar (HTML drag and drop, as the
// old web did; the phone's menu only goes to them). What a drop does is in
// favorites-move.ts; this reads the rows under the pointer and draws nothing
// itself: the section asks `lineAt` and `intoFolder` for its rows.
//
// The rows carry `data-fav-type`, `data-fav-id` and `data-fav-in` (the folder
// they are in, empty in the root); the list around them is the drop target, so
// a gap between rows or the room under the list takes a drop too.
import { type DragEvent, type KeyboardEvent, useState } from "react";
import type { FavoriteList } from "./favorites";
import {
  type DropRow,
  dropAt,
  type EntryRef,
  type FavoriteDrop,
  type FavoriteMove,
  isNoopMove,
  stepMove,
} from "./favorites-move";

const MIME = {
  item: "application/x-trameria-favorite",
  folder: "application/x-trameria-favorite-folder",
} as const;

/** 0..1, as a document block's: a hint of what is carried; the line is what matters. */
const GHOST_OPACITY = "0.15";

function heldType(e: DragEvent): EntryRef["type"] | null {
  const types = Array.from(e.dataTransfer.types);
  if (types.includes(MIME.item)) return "item";
  if (types.includes(MIME.folder)) return "folder";
  return null;
}

function rowsIn(list: Element): DropRow[] {
  return Array.from(
    list.querySelectorAll<HTMLElement>("[data-fav-id]"),
    (el) => {
      const box = el.getBoundingClientRect();
      return {
        entry: {
          type: el.dataset.favType === "folder" ? "folder" : "item",
          id: el.dataset.favId ?? "",
        },
        folderId: el.dataset.favIn || null,
        top: box.top,
        bottom: box.bottom,
      };
    },
  );
}

/**
 * A faint copy of the row under the pointer where it was taken. It lives under
 * <body> in its own stacking context (Chromium draws the image from the nearest
 * one; inside the sidebar the drawer's clip turned it blank).
 */
function setGhost(e: DragEvent<HTMLElement>) {
  const source = e.currentTarget;
  const ghost = document.createElement("div");
  ghost.setAttribute("aria-hidden", "true");
  Object.assign(ghost.style, {
    position: "absolute",
    top: "-10000px",
    left: "0",
    width: `${source.offsetWidth}px`,
    pointerEvents: "none",
    isolation: "isolate",
  });
  const clone = source.cloneNode(true) as HTMLElement;
  clone.style.opacity = GHOST_OPACITY;
  ghost.append(clone);
  document.body.append(ghost);
  const box = source.getBoundingClientRect();
  e.dataTransfer.setDragImage(ghost, e.clientX - box.left, e.clientY - box.top);
  const cleanup = () => {
    ghost.remove();
    document.removeEventListener("dragend", cleanup);
    document.removeEventListener("drop", cleanup);
  };
  document.addEventListener("dragend", cleanup);
  document.addEventListener("drop", cleanup);
}

export function useFavoriteDrag(
  /** The list as shown (kinds this app does not show left out). */
  list: FavoriteList,
  onMove: (move: FavoriteMove) => void,
) {
  const [held, setHeld] = useState<EntryRef | null>(null);
  const [drop, setDrop] = useState<FavoriteDrop | null>(null);
  const clear = () => {
    setHeld(null);
    setDrop(null);
  };
  const isHeld = (r: EntryRef) => held?.type === r.type && held.id === r.id;
  const over = (e: DragEvent<HTMLElement>) => {
    if (!heldType(e)) return;
    // A drop target by the event alone (state may not have caught up).
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (!held) return;
    const d = dropAt(rowsIn(e.currentTarget), held, e.clientY);
    const next = isNoopMove(list, d) ? null : d;
    if (JSON.stringify(next) !== JSON.stringify(drop)) setDrop(next);
  };

  return {
    /** The row being carried (drawn faint). */
    isHeld,
    /** The line at the top of this row: the drop goes before it. */
    lineBefore: (r: EntryRef, folderId: string | null) =>
      drop !== null &&
      !drop.into &&
      drop.before?.type === r.type &&
      drop.before.id === r.id &&
      drop.folderId === folderId,
    /** The line under the last of a container: the drop goes to its end. */
    lineAtEnd: (folderId: string | null) =>
      drop !== null &&
      !drop.into &&
      drop.before === null &&
      drop.folderId === folderId,
    /** The folder lit up: the drop goes into it. */
    intoFolder: (id: string) => drop?.into === true && drop.folderId === id,

    listProps: {
      // On entering a row as well as moving over it: the line follows at
      // once, not only at the next dragover.
      onDragEnter: over,
      onDragOver: over,
      onDragLeave: (e: DragEvent<HTMLElement>) => {
        const to = e.relatedTarget;
        if (to instanceof Node && e.currentTarget.contains(to)) return;
        setDrop(null);
      },
      onDrop: (e: DragEvent<HTMLElement>) => {
        if (!heldType(e)) return;
        e.preventDefault();
        const d = held
          ? dropAt(rowsIn(e.currentTarget), held, e.clientY)
          : null;
        clear();
        if (d && !isNoopMove(list, d)) onMove(d);
      },
    },

    /** On a row that can be carried. */
    rowProps: (r: EntryRef, folderId: string | null) => ({
      draggable: true,
      "data-fav-type": r.type,
      "data-fav-id": r.id,
      "data-fav-in": folderId ?? "",
      onDragStart: (e: DragEvent<HTMLElement>) => {
        e.stopPropagation();
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData(MIME[r.type], r.id);
        setGhost(e);
        setHeld(r);
      },
      onDragEnd: clear,
    }),

    /** On the row's focusable part: Alt+↑ / Alt+↓ moves it one step. */
    keyProps: (r: EntryRef) => ({
      "aria-keyshortcuts": "Alt+ArrowUp Alt+ArrowDown",
      onKeyDown: (e: KeyboardEvent) => {
        if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
        const move = stepMove(list, r, e.key === "ArrowUp");
        if (!move) return;
        e.preventDefault();
        onMove(move);
      },
    }),
  };
}
